<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
    <TransitionRoot as="template" :show="sidebarOpen">
      <Dialog as="div" class="relative z-50 lg:hidden" @close="sidebarOpen = false">
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <DialogPanel id="admin-sidebar" class="relative flex w-64 max-w-full flex-col bg-white dark:bg-gray-800 shadow-lg">
              <div class="flex items-center justify-between h-16 px-6 border-b border-gray-200 dark:border-gray-700">
                <div class="flex items-center space-x-2">
                  <div class="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <span class="text-white font-bold text-sm">A</span>
                  </div>
                  <span class="text-lg font-semibold text-gray-900 dark:text-white">Admin Panel</span>
                </div>
                <button
                  type="button"
                  @click="sidebarOpen = false"
                  aria-label="Close sidebar"
                  class="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <XMarkIcon class="w-6 h-6" />
                </button>
              </div>

              <div class="p-6 border-b border-gray-200 dark:border-gray-700">
                <div class="flex items-center space-x-3">
                  <div class="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                    <UserIcon class="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ currentAdminUser?.displayName || 'Admin User' }}
                    </p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      {{ currentAdminUser?.role || 'Administrator' }}
                    </p>
                  </div>
                </div>
              </div>

              <nav class="mt-6 px-3 overflow-y-auto">
                <div class="space-y-1">
                  <button v-for="item in navigationItems" :key="item.id" type="button" @click="handleMobileSectionChange(item.id)" :aria-current="activeSection === item.id ? 'page' : undefined" :class="[
                    'w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                    activeSection === item.id
                      ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  ]">
                    <component :is="item.icon" class="w-5 h-5 mr-3" />
                    {{ item.name }}
                    <span v-if="item.badge"
                      class="ml-auto bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300 text-xs px-2 py-1 rounded-full">
                      {{ item.badge }}
                    </span>
                  </button>
                </div>

                <div class="mt-8">
                  <h3 class="px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Quick Actions
                  </h3>
                  <div class="mt-3 space-y-1">
                    <button type="button" @click="openMobileUploadModal"
                      class="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                      <PlusIcon class="w-5 h-5 mr-3" />
                      Upload Wallpaper
                    </button>
                    <button type="button" @click="openMobileCategoryModal"
                      class="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                      <FolderPlusIcon class="w-5 h-5 mr-3" />
                      Add Category
                    </button>
                  </div>
                </div>
              </nav>

              <div class="mt-auto p-4 border-t border-gray-200 dark:border-gray-700">
                <button type="button" @click="handleMobileSignOut"
                  class="w-full flex items-center px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                  <ArrowRightStartOnRectangleIcon class="w-5 h-5 mr-3" />
                  Sign Out
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <aside id="admin-sidebar-desktop" class="hidden lg:flex lg:w-64 lg:flex-col bg-white dark:bg-gray-800 shadow-lg">
      <div class="relative flex min-h-screen flex-col">
        <div class="flex items-center justify-between h-16 px-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center space-x-2">
            <div class="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <span class="text-white font-bold text-sm">A</span>
            </div>
            <span class="text-lg font-semibold text-gray-900 dark:text-white">Admin Panel</span>
          </div>
        </div>

        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center space-x-3">
            <div class="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
              <UserIcon class="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p class="text-sm font-medium text-gray-900 dark:text-white">
                {{ currentAdminUser?.displayName || 'Admin User' }}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ currentAdminUser?.role || 'Administrator' }}
              </p>
            </div>
          </div>
        </div>

        <nav class="mt-6 px-3 flex-1 overflow-y-auto">
          <div class="space-y-1">
            <button v-for="item in navigationItems" :key="item.id" type="button" @click="activeSection = item.id" :aria-current="activeSection === item.id ? 'page' : undefined" :class="[
              'w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
              activeSection === item.id
                ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            ]">
              <component :is="item.icon" class="w-5 h-5 mr-3" />
              {{ item.name }}
              <span v-if="item.badge"
                class="ml-auto bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300 text-xs px-2 py-1 rounded-full">
                {{ item.badge }}
              </span>
            </button>
          </div>

          <div class="mt-8">
            <h3 class="px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Quick Actions
            </h3>
            <div class="mt-3 space-y-1">
              <button type="button" @click="showUploadModal = true"
                class="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <PlusIcon class="w-5 h-5 mr-3" />
                Upload Wallpaper
              </button>
              <button type="button" @click="showCategoryModal = true"
                class="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <FolderPlusIcon class="w-5 h-5 mr-3" />
                Add Category
              </button>
            </div>
          </div>
        </nav>

        <div class="mt-auto p-4 border-t border-gray-200 dark:border-gray-700">
          <button type="button" @click="handleSignOut"
            class="w-full flex items-center px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
            <ArrowRightStartOnRectangleIcon class="w-5 h-5 mr-3" />
            Sign Out
          </button>
        </div>
      </div>
    </aside>

    <!-- Main content -->
    <div class="flex-1 flex flex-col min-w-0">
      <!-- Top bar -->
      <div class="sticky top-0 z-30 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between h-16 px-6">
          <div class="flex items-center space-x-4">
            <button @click="sidebarOpen = true"
              aria-label="Open sidebar"
              aria-controls="admin-sidebar"
              :aria-expanded="sidebarOpen"
              class="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">
              <Bars3Icon class="w-6 h-6" />
            </button>
            <div>
              <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
                {{ currentSectionTitle }}
              </h1>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                {{ currentSectionDescription }}
              </p>
            </div>
          </div>

          <div class="flex items-center space-x-4">
            <!-- Notifications -->
            <button class="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Notifications">
              <BellIcon class="w-6 h-6" />
            </button>

            <!-- Theme toggle -->
            <SwitchGroup>
              <div class="flex items-center">
                <SwitchLabel class="sr-only">Toggle theme</SwitchLabel>
                <Switch v-model="isDark" @update:model-value="toggleTheme" :class="[
                  isDark ? 'bg-purple-600' : 'bg-gray-200',
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-offset-2'
                ]">
                  <span :class="[
                    isDark ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                  ]">
                    <span :class="[
                      isDark ? 'opacity-0 duration-100 ease-out' : 'opacity-100 duration-200 ease-in',
                      'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity'
                    ]" aria-hidden="true">
                      <SunIcon class="h-3 w-3 text-gray-400" />
                    </span>
                    <span :class="[
                      isDark ? 'opacity-100 duration-200 ease-in' : 'opacity-0 duration-100 ease-out',
                      'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity'
                    ]" aria-hidden="true">
                      <MoonIcon class="h-3 w-3 text-purple-600" />
                    </span>
                  </span>
                </Switch>
              </div>
            </SwitchGroup>
          </div>
        </div>
      </div>

      <!-- Page content -->
      <main class="flex-1 p-6 overflow-auto">
        <!-- Dashboard Overview -->
        <div v-if="activeSection === 'dashboard'" class="space-y-6">
          <!-- Enhanced Stats Overview -->
          <WallpaperStats />

          <!-- Dashboard Grid - 3 columns for better layout -->
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <!-- Blog Stats Widget -->
            <BlogStatsWidget
              @navigate="activeSection = $event"
              @create-post="handleCreatePost"
              @view-blog="handleViewBlog"
            />

            <!-- Top Wallpapers Widget -->
            <TopWallpapersWidget />

            <!-- Recent Activity Widget -->
            <RecentActivityWidget />
          </div>

          <!-- Additional Dashboard Widgets -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Quick Actions Panel -->
            <div class="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Quick Actions</h3>
              <div class="grid grid-cols-2 gap-3">
                <button
                  @click="showUploadModal = true"
                  class="flex items-center justify-center px-4 py-3 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                >
                  <PlusIcon class="w-5 h-5 mr-2" />
                  Upload Wallpaper
                </button>
                <button
                  @click="showCategoryModal = true"
                  class="flex items-center justify-center px-4 py-3 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                >
                  <FolderPlusIcon class="w-5 h-5 mr-2" />
                  Add Category
                </button>
                <button
                  @click="activeSection = 'wallpapers'"
                  class="flex items-center justify-center px-4 py-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                >
                  <PhotoIcon class="w-5 h-5 mr-2" />
                  Manage Wallpapers
                </button>
                <button
                  @click="activeSection = 'analytics'"
                  class="flex items-center justify-center px-4 py-3 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                >
                  <ChartBarIcon class="w-5 h-5 mr-2" />
                  View Analytics
                </button>
              </div>
            </div>

            <!-- System Status Panel -->
            <div class="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">System Status</h3>
              <div class="space-y-3">
                <div class="flex items-center justify-between">
                  <span class="text-sm text-gray-600 dark:text-gray-400">Database Status</span>
                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
                    <div class="w-2 h-2 bg-green-400 rounded-full mr-1"></div>
                    Online
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm text-gray-600 dark:text-gray-400">Storage Status</span>
                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
                    <div class="w-2 h-2 bg-green-400 rounded-full mr-1"></div>
                    Healthy
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm text-gray-600 dark:text-gray-400">CDN Status</span>
                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
                    <div class="w-2 h-2 bg-green-400 rounded-full mr-1"></div>
                    Active
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-sm text-gray-600 dark:text-gray-400">Last Backup</span>
                  <span class="text-xs text-gray-500 dark:text-gray-400">2 hours ago</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Wallpapers Section -->
        <div v-if="activeSection === 'wallpapers'">
          <WallpaperManagement :key="wallpaperManagementKey" />
        </div>

        <!-- Categories Section -->
        <div v-if="activeSection === 'categories'">
          <CategoryManagement />
        </div>

        <!-- Users Section -->
        <div v-if="activeSection === 'users'">
          <AdminUserManagement />
        </div>

        <!-- Blog Section -->
        <div v-if="activeSection === 'blog'">
          <BlogManagement />
        </div>

        <!-- Analytics Section -->
        <div v-if="activeSection === 'analytics'" class="space-y-6">
          <div class="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Analytics Dashboard</h3>
            <p class="text-gray-500 dark:text-gray-400">Advanced analytics and reporting features coming soon...</p>
          </div>
        </div>

        <!-- Settings Section -->
        <div v-if="activeSection === 'settings'" class="space-y-6">
          <div class="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Application Settings</h3>
            <p class="text-gray-500 dark:text-gray-400">System configuration and settings management coming soon...</p>
          </div>
        </div>
      </main>
    </div>

    <!-- Upload Modal -->
    <WallpaperUploadModal v-if="showUploadModal" :isOpen="showUploadModal" @close="showUploadModal = false"
      @upload-success="handleUploadSuccess" />

    <!-- Category Modal -->
    <CategoryModal v-if="showCategoryModal" :isOpen="showCategoryModal" @close="showCategoryModal = false"
      @success="handleCategorySuccess" />
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  PhotoIcon,
  FolderIcon,
  UsersIcon,
  ChartBarIcon,
  CogIcon,
  UserIcon,
  BellIcon,
  SunIcon,
  MoonIcon,
  PlusIcon,
  FolderPlusIcon,
  ArrowRightStartOnRectangleIcon,
  DocumentTextIcon
} from '@heroicons/vue/24/outline';
import {
  Switch,
  SwitchGroup,
  SwitchLabel
} from '@headlessui/vue';
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue';
import { useAdmin } from '../composables/useAdmin';
import { authService } from '../firebase/services/authService';
import { ref, computed, onMounted, defineAsyncComponent } from 'vue';

// Async components
const WallpaperManagement = defineAsyncComponent(() =>
  import('../components/WallpaperManagement.vue')
);

const WallpaperStats = defineAsyncComponent(() =>
  import('../components/WallpaperStats.vue')
);

const WallpaperUploadModal = defineAsyncComponent(() =>
  import('../components/WallpaperUploadModal.vue')
);

const CategoryModal = defineAsyncComponent(() =>
  import('../components/CategoryModal.vue')
);

const CategoryManagement = defineAsyncComponent(() =>
  import('../components/CategoryManagement.vue')
);

const AdminUserManagement = defineAsyncComponent(() =>
  import('../components/AdminUserManagement.vue')
);

const BlogManagement = defineAsyncComponent(() =>
  import('../components/admin/BlogManagement.vue')
);

const BlogStatsWidget = defineAsyncComponent(() =>
  import('../components/admin/BlogStatsWidget.vue')
);

const TopWallpapersWidget = defineAsyncComponent(() =>
  import('../components/admin/TopWallpapersWidget.vue')
);

const RecentActivityWidget = defineAsyncComponent(() =>
  import('../components/admin/RecentActivityWidget.vue')
);

// Theme type for better type safety
type Theme = 'light' | 'dark' | 'system';

// Extend Window interface to include __setPreferredTheme
declare global {
  interface Window {
    __setPreferredTheme?: (theme: Theme) => void;
  }
}

// Router
const router = useRouter();

// Admin management
const { currentAdminUser } = useAdmin();

// State
const sidebarOpen = ref(false);
const activeSection = ref('dashboard');
const showUploadModal = ref(false);
const showCategoryModal = ref(false);
const isDark = ref(false);
const wallpaperManagementKey = ref(0); // Key to force refresh of WallpaperManagement component

// Remove unused recentActivities since we now use RecentActivityWidget

// Navigation items
const navigationItems = computed(() => [
  {
    id: 'dashboard',
    name: 'Dashboard',
    icon: HomeIcon,
    badge: null
  },
  {
    id: 'wallpapers',
    name: 'Wallpapers',
    icon: PhotoIcon,
    badge: null
  },
  {
    id: 'categories',
    name: 'Categories',
    icon: FolderIcon,
    badge: null
  },
  {
    id: 'blog',
    name: 'Blog',
    icon: DocumentTextIcon,
    badge: null
  },
  {
    id: 'users',
    name: 'Users & Admins',
    icon: UsersIcon,
    badge: null
  },
  {
    id: 'analytics',
    name: 'Analytics',
    icon: ChartBarIcon,
    badge: null
  },
  {
    id: 'settings',
    name: 'Settings',
    icon: CogIcon,
    badge: null
  }
]);

// Computed
const currentSectionTitle = computed(() => {
  const section = navigationItems.value.find((item) => item.id === activeSection.value);
  return section?.name || 'Dashboard';
});

const currentSectionDescription = computed(() => {
  const descriptions: Record<string, string> = {
    dashboard: 'Overview of your wallpaper application',
    wallpapers: 'Manage and organize wallpapers',
    categories: 'Create and manage wallpaper categories',
    blog: 'Create and manage blog posts',
    users: 'Manage admin users and permissions',
    analytics: 'View detailed analytics and reports',
    settings: 'Configure application settings'
  };
  return descriptions[activeSection.value as keyof typeof descriptions] || '';
});

// Methods
const toggleTheme = () => {
  isDark.value = !isDark.value;
  if (isDark.value) {
    document.documentElement.classList.add('dark');
    localStorage.setItem('theme', 'dark');
  } else {
    document.documentElement.classList.remove('dark');
    localStorage.setItem('theme', 'light');
  }

  // Call global theme handler if available
  if (window.__setPreferredTheme) {
    window.__setPreferredTheme(isDark.value ? 'dark' : 'light');
  }
};

const handleSignOut = async (): Promise<void> => {
  try {
    await authService.signOut();
    router.push('/');
  } catch (error) {
    console.error('Error signing out:', error);
  }
};

const handleMobileSectionChange = (sectionId: string): void => {
  sidebarOpen.value = false;
  activeSection.value = sectionId;
};

const openMobileUploadModal = (): void => {
  sidebarOpen.value = false;
  showUploadModal.value = true;
};

const openMobileCategoryModal = (): void => {
  sidebarOpen.value = false;
  showCategoryModal.value = true;
};

const handleMobileSignOut = async (): Promise<void> => {
  sidebarOpen.value = false;
  await handleSignOut();
};

const handleUploadSuccess = () => {
  showUploadModal.value = false;
  // Force refresh of the WallpaperManagement component by changing its key
  wallpaperManagementKey.value += 1;
};

const handleCategorySuccess = () => {
  showCategoryModal.value = false;
  // Refresh data if needed
};

const handleCreatePost = () => {
  activeSection.value = 'blog';
  // The BlogManagement component will handle opening the editor
};

const handleViewBlog = () => {
  // Open blog in new tab
  window.open('/blog', '_blank');
};

// Initialize
onMounted(async () => {
  // Check initial theme
  const savedTheme = localStorage.getItem('theme');
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  isDark.value = savedTheme === 'dark' || (!savedTheme && prefersDark);

  if (isDark.value) {
    document.documentElement.classList.add('dark');
  }

  // Check admin authentication
  if (!currentAdminUser.value) {
    console.log('No admin user found, redirecting to admin login...');
    router.push('/admin');
  }
});
</script>